import React, { useState, useEffect } from "react";
import CommonLayout from "../../layouts/CommonLayout";
import { allAPiServicesCall } from "../../services/apiServices";
import { getAuthConfig } from "../../services/apiUtils";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ROUTE_CONSTANT } from "../../routes/constant";
import { Form } from "react-bootstrap";
import SetTrendArtistPriorityModel from "../Model/SetTrendArtistPriorityModel";
import SetRecArtistPriorityModel from "../Model/SetRecArtistPriorityModel";
import SetTopAlbumPriorityModel from "../Model/SetTopAlbumPriorityModel";
import { artistImageUrl, albumImageUrl, albumArtistName, formatViewCount } from "../../utils/utils";
import {
  FaFire,
  FaStar,
  FaCompactDisc,
  FaChevronRight,
  FaFilter,
  FaSortAmountDown
} from "react-icons/fa";
import "./Dashboard.css";

function Dashboard() {
  const [trendingArtists, setTrendingArtists] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [showTrendArtistPrioritEditModal, setTrendArtistPrioritShowEditModal] = useState(false);
  const [showRecArtistPrioritEditModal, setRecArtistPrioritShowEditModal] = useState(false);
  const [showTopAlbumPrioritEditModal, setTopAlbumPrioritShowEditModal] = useState(false);
  const [trendArtistEditData, setTrendArtistDetailEditData] = useState({});
  const [recArtistEditData, setRecArtistDetailEditData] = useState({});
  const [topAlbumEditData, setTopAlbumDetailEditData] = useState({});

  // Sort states
  const [sortTrending, setSortTrending] = useState("latest");
  const [sortRecommended, setSortRecommended] = useState("latest");
  const [sortAlbums, setSortAlbums] = useState("latest");

  // Global genre filter
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState("");

  const navigate = useNavigate();

  // Fetch genres for global filter
  const fetchGenres = async () => {
    try {
      const response = await allAPiServicesCall.getGenres(
        { limit: 100 },
        getAuthConfig(),
        navigate
      );
      if (response?.data && Array.isArray(response.data)) {
        setGenres(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch genres:", error);
    }
  };

  useEffect(() => {
    fetchGenres();
  }, []);

  // ---- Fetchers ----
  const fetchTrendingArtists = async (page = 1, limit = 10, sort = "latest", genre = selectedGenre) => {
    setLoading(true);
    try {
      const params = { limit, page, sort };
      if (genre) params.genre = genre;
      const response = await allAPiServicesCall.getTrendingArtists(
        params,
        getAuthConfig()
      );
      const { totalPages, trendingList } = response.data;
      if (Array.isArray(trendingList)) {
        setTrendingArtists(trendingList);
        setTotalPages(totalPages || 1);
      } else {
        console.warn("No trending data found or invalid format");
      }
    } catch (error) {
      console.error("Error fetching trending artists:", error);
      toast.error("Failed to fetch trending artists");
    } finally {
      setLoading(false);
    }
  };

  const getRecommendedArtists = async (page = 1, limit = 10, sort = "latest", genre = selectedGenre) => {
    setLoading(true);
    try {
      const params = { limit, page, sort };
      if (genre) params.genre = genre;
      const response = await allAPiServicesCall.getRecommendedArtists(
        params,
        getAuthConfig()
      );
      const { recommendedList } = response.data;
      if (Array.isArray(recommendedList)) {
        setSearchResults(recommendedList);
      } else {
        console.warn("No recommended data found or invalid format");
      }
    } catch (error) {
      console.error("Error fetching recommended artists:", error);
      toast.error("Failed to fetch recommended artists");
    } finally {
      setLoading(false);
    }
  };

  const fetchTopExploreAlbums = async (page = 1, limit = 10, sort = "latest", genre = selectedGenre) => {
    setLoading(true);
    try {
      const params = { limit, page, sort };
      if (genre) params.genre = genre;
      const response = await allAPiServicesCall.getTopExploreAlbums(
        params,
        getAuthConfig()
      );
      if (response?.success && Array.isArray(response?.data?.albums)) {
        setAlbums(response.data.albums);
      } else if (response?.data?.topArtistList) {
        setAlbums(response.data.topArtistList);
      } else {
        toast.warn("No albums found or invalid response.");
      }
    } catch (error) {
      console.error("Error fetching top explore albums:", error);
      toast.error("Failed to fetch top explore albums.");
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchTrendingArtists(1, 10, sortTrending, selectedGenre);
    getRecommendedArtists(1, 10, sortRecommended, selectedGenre);
    fetchTopExploreAlbums(1, 10, sortAlbums, selectedGenre);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch on sort or genre change
  useEffect(() => {
    fetchTrendingArtists(1, 10, sortTrending, selectedGenre);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortTrending, selectedGenre]);

  useEffect(() => {
    getRecommendedArtists(1, 10, sortRecommended, selectedGenre);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortRecommended, selectedGenre]);

  useEffect(() => {
    fetchTopExploreAlbums(1, 10, sortAlbums, selectedGenre);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortAlbums, selectedGenre]);

  // Navigation - pass genre as query param
  const handleViewMoreTrend = () => {
    const url = selectedGenre
      ? `${ROUTE_CONSTANT.COMMON.TRENDING_ARTIST}?genre=${encodeURIComponent(selectedGenre)}`
      : ROUTE_CONSTANT.COMMON.TRENDING_ARTIST;
    navigate(url);
  };

  const handleViewMoreRec = () => {
    const url = selectedGenre
      ? `${ROUTE_CONSTANT.COMMON.RECOMMENDED_ARTIST}?genre=${encodeURIComponent(selectedGenre)}`
      : ROUTE_CONSTANT.COMMON.RECOMMENDED_ARTIST;
    navigate(url);
  };

  const handleViewMoreTrendTop = () => {
    const url = selectedGenre
      ? `${ROUTE_CONSTANT.COMMON.TOP_EXPLORE}?genre=${encodeURIComponent(selectedGenre)}`
      : ROUTE_CONSTANT.COMMON.TOP_EXPLORE;
    navigate(url);
  };

  // Shared dropdown UI
  const SortSelect = ({ value, onChange }) => (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="sort-select"
      aria-label="Sort items"
    >
      <option value="latest">Latest</option>
      <option value="priority">Priority</option>
      <option value="name">Name</option>
    </select>
  );

  const setTrendArtistPriority = (ArtistId) => {
    setTrendArtistPrioritShowEditModal(true);
    setTrendArtistDetailEditData(ArtistId);
  };

  const setRecArtistPriority = (ArtistId) => {
    setRecArtistPrioritShowEditModal(true);
    setRecArtistDetailEditData(ArtistId);
  };

  const setTopAlbumPriority = (ArtistId) => {
    setTopAlbumPrioritShowEditModal(true);
    setTopAlbumDetailEditData(ArtistId);
  };

  return (
    <CommonLayout>
      <div className="dashboard-container">
        {/* Global Genre Filter */}
        <div className="genre-filter-container mb-4">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <Form.Label className="mb-0 fw-bold d-flex align-items-center gap-2">
              <FaFilter style={{ color: '#0031E2' }} /> Genre Context:
            </Form.Label>
            <Form.Select
              value={selectedGenre}
              onChange={(e) => setSelectedGenre(e.target.value)}
              style={{ width: 'auto', minWidth: '220px' }}
            >
              <option value="">All Genres (default)</option>
              {genres.map((genre) => (
                <option key={genre._id} value={genre.genreName}>
                  {genre.genreName}
                </option>
              ))}
            </Form.Select>
            {selectedGenre && (
              <span className="text-info small">
                Showing and managing content for "{selectedGenre}" genre
              </span>
            )}
          </div>
        </div>

        {/* TRENDING ARTISTS */}
        <div className="section-header">
          <h3 className="section-title">
            <FaFire style={{ color: '#0031E2', marginRight: '0.5rem' }} />
            TRENDING ARTISTS {selectedGenre && <span style={{ opacity: 0.7, fontSize: '0.85em' }}>({selectedGenre})</span>}
          </h3>
          <div className="d-flex align-items-center gap-2">
            <FaSortAmountDown style={{ color: '#9CA3AF' }} />
            <SortSelect value={sortTrending} onChange={setSortTrending} />
          </div>
        </div>

        {loading ? (
          <p className="dashboard-loading">Loading artists...</p>
        ) : (
          <div className="artist-section">
            <div className="artist-cards">
              {trendingArtists.slice(0, 4).map((artist, index) => (
                <div key={index} className="artist-card">
                  <img
                    src={artistImageUrl(artist)}
                    alt={artist.artist_name}
                    className="artist-image"
                    onClick={() => navigate(`/artist/albums-songs/${artist.artist_id}`)}
                    style={{ cursor: 'pointer' }}
                  />
                  <h4 className="artist-name">{artist.artist_name}</h4>
                  {formatViewCount(artist.spotifyListeners ?? artist.popularity) && (
                    <p className="text-muted small mb-1">{formatViewCount(artist.spotifyListeners ?? artist.popularity)} listeners</p>
                  )}
                  <button
                    className="btn btn-primary"
                    onClick={() => setTrendArtistPriority(artist)}
                  >
                    Set Priority
                  </button>
                </div>
              ))}
            </div>
            <button
              className="view-more-btn"
              onClick={handleViewMoreTrend}
              aria-label="View more trending artists"
              title="View All Trending Artists"
            >
              <FaChevronRight />
            </button>
          </div>
        )}

        {/* RECOMMENDED ARTISTS */}
        <div className="section-header">
          <h3 className="section-title">
            <FaStar style={{ color: '#0031E2', marginRight: '0.5rem' }} />
            RECOMMENDED ARTISTS {selectedGenre && <span style={{ opacity: 0.7, fontSize: '0.85em' }}>({selectedGenre})</span>}
          </h3>
          <div className="d-flex align-items-center gap-2">
            <FaSortAmountDown style={{ color: '#9CA3AF' }} />
            <SortSelect value={sortRecommended} onChange={setSortRecommended} />
          </div>
        </div>

        {loading ? (
          <p className="dashboard-loading">Loading artists...</p>
        ) : (
          <div className="artist-section">
            <div className="artist-cards">
              {searchResults.slice(0, 4).map((artist, index) => (
                <div key={index} className="artist-card">
                  <img
                    src={artistImageUrl(artist)}
                    alt={artist.artist_name}
                    className="artist-image"
                    onClick={() => navigate(`/artist/albums-songs/${artist.artist_id}`)}
                    style={{ cursor: 'pointer' }}
                  />
                  <h4 className="artist-name">{artist.artist_name}</h4>
                  {formatViewCount(artist.spotifyListeners ?? artist.popularity) && (
                    <p className="text-muted small mb-1">{formatViewCount(artist.spotifyListeners ?? artist.popularity)} listeners</p>
                  )}
                  <button
                    className="btn btn-primary"
                    onClick={() => setRecArtistPriority(artist)}
                  >
                    Set Priority
                  </button>
                </div>
              ))}
            </div>
            <button
              className="view-more-btn"
              onClick={handleViewMoreRec}
              aria-label="View more recommended artists"
              title="View All Recommended Artists"
            >
              <FaChevronRight />
            </button>
          </div>
        )}

        {/* TOP EXPLORE ALBUMS */}
        <div className="section-header">
          <h3 className="section-title">
            <FaCompactDisc style={{ color: '#0031E2', marginRight: '0.5rem' }} />
            TOP EXPLORE ALBUMS {selectedGenre && <span style={{ opacity: 0.7, fontSize: '0.85em' }}>({selectedGenre})</span>}
          </h3>
          <div className="d-flex align-items-center gap-2">
            <FaSortAmountDown style={{ color: '#9CA3AF' }} />
            <SortSelect value={sortAlbums} onChange={setSortAlbums} />
          </div>
        </div>

        {loading ? (
          <p className="dashboard-loading">Loading albums...</p>
        ) : (
          <div className="artist-section">
            <div className="artist-cards">
              {albums.slice(0, 4).map((album, index) => (
                <div key={index} className="artist-card">
                  <img
                    src={albumImageUrl(album)}
                    alt={album.album_name}
                    className="artist-image"
                    onClick={() => navigate(`/album/${album.album_id}`)}
                    style={{ cursor: 'pointer' }}
                  />
                  <h4 className="artist-name">{album.album_name}</h4>
                  <p className="text-muted small mb-1">{albumArtistName(album)}</p>
                  {formatViewCount(album.popularity ?? album.spotifyListeners ?? album.totalPlays) && (
                    <p className="text-muted small mb-1">{formatViewCount(album.popularity ?? album.spotifyListeners ?? album.totalPlays)} plays</p>
                  )}
                  <button
                    className="btn btn-primary"
                    onClick={() => setTopAlbumPriority(album)}
                  >
                    Set Priority
                  </button>
                </div>
              ))}
            </div>
            <button
              className="view-more-btn"
              onClick={handleViewMoreTrendTop}
              aria-label="View more albums"
              title="View All Top Albums"
            >
              <FaChevronRight />
            </button>
          </div>
        )}
      </div>

      <SetTrendArtistPriorityModel
        show={showTrendArtistPrioritEditModal}
        handleClose={() => setTrendArtistPrioritShowEditModal(false)}
        artistDetails={trendArtistEditData}
      />

      <SetRecArtistPriorityModel
        show={showRecArtistPrioritEditModal}
        handleClose={() => setRecArtistPrioritShowEditModal(false)}
        artistDetails={recArtistEditData}
      />

      <SetTopAlbumPriorityModel
        show={showTopAlbumPrioritEditModal}
        handleClose={() => setTopAlbumPrioritShowEditModal(false)}
        albumDetails={topAlbumEditData}
      />
    </CommonLayout>
  );
}

export default Dashboard;
